import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  InputSignal,
} from '@angular/core';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { input } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import { RoutePaths } from '../app.routes';
import {
  ApproveRejectRegClickEvent,
  ApproveRejectRegComponent,
} from '../shared/components/approve-reject-reg/approve-reject-reg.component';
import { ApproveRejectRegViewModel } from '../shared/components/approve-reject-reg/viewmodel/approve-reject-reg.viewmodel';
import { AppStringsPipe } from '../shared/pipes/appstrings.pipe';
import { ReactiveFormsModule } from '@angular/forms';
import { DataStoreReaderService } from '../shared/services/data-store/viewmodels/data-store-reader-viewmodels.service';
import { SectionIdTrialIdUuid } from '../shared/viewmodels/section-id-trial-id-uuid';
import { SectionOverviewBatchRowViewModel } from '../shared/viewmodels/section-overview/section-overview-batch-row.viewmodel';
import { SectionOverviewBatchRowWeighingViewModel } from '../shared/viewmodels/section-overview/section-overview-batch-row-weighing.viewmodel';
import { StringUuid } from '../shared/domain/model/uuid/string-uuid';
import { ApprovalStatusTypeEnum } from '../shared/domain/model/model-enums';
import { WeighingType } from '../shared/domain/model/weighing-type';
import { ApprovalStatusType } from '../shared/domain/model/approval-status-type';


// enum ActionApproveRejectType {
//   INSERTION,
//   WEIGHING1,
//   WEIGHING2,
//   ENDWEIGHING,
// }

@Component({
  selector: 'app-section-overview',
  imports: [
    ApproveRejectRegComponent,
    AppStringsPipe,
    DatePipe,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    NgClass,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './section-overview.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionOverviewComponent implements AfterViewInit {
  private readonly CLS_REG_COMPLETED = 'registration-completed-status';
  private readonly CLS_REG_NOT_APPROVED = 'registration-not-approved-status';
  private readonly CLS_REG_APPROVED = 'registration-approved-status';
  private readonly CLS_CELL_ACTION_GREEN = 'cell-action-green';
  private readonly CLS_CELL_ACTION_RED = 'cell-action-red';

  //protected readonly ATTR_NAME_ACTION_TYPE = 'action-type';
  protected readonly ATTR_NAME_BATCH_ID = 'batch-id';

  // protected readonly ATTR_VALUE_APPROVE_INSERTION = 'ApproveInsertion';
  // protected readonly ATTR_VALUE_REJECT_INSERTION = 'RejectInsertion';

  // protected readonly ATTR_VALUE_APPROVE_WEIGHING1 = 'ApproveWeighing1';
  // protected readonly ATTR_VALUE_REJECT_WEIGHING1 = 'RejectWeighing1';

  // protected readonly ATTR_VALUE_APPROVE_WEIGHING2 = 'ApproveWeighing2';
  // protected readonly ATTR_VALUE_REJECT_WEIGHING2 = 'RejectWeighing2';

  // protected readonly ATTR_VALUE_REG_INSERTION = 'RegInsertion';
  // protected readonly ATTR_VALUE_REG_WEIGHING = 'RegWeighing';
  // protected readonly ATTR_VALUE_REG_END_WEIGHING = 'RegEndWeighing';

  protected readonly COLUMN_INSERTION_BATCH = 'insertionBatch';
  protected readonly COLUMN_INSERTION_SEX = 'insertionSex';
  protected readonly COLUMN_INSERTION_AVG_WEIGHT = 'insertionAvgWeight';
  protected readonly COLUMN_WEIGHING_DATE = 'weighingDate-';
  protected readonly COLUMN_WEIGHING_AVG_WEIGHT = 'weighingAvgWeight-';
  protected readonly COLUMN_END_WEIGHING_DATE = 'endWeighingDate';
  protected readonly COLUMN_END_WEIGHING_AVG_WEIGHT = 'endWeighingAvgWeight';

  protected readonly HEADER_COLUMN_INSERTION_BATCH = 'header-row-batch';
  protected readonly HEADER_COLUMN_INSERTION = 'header-row-insertion';
  protected readonly HEADER_COLUMN_INTERIM_WEIGHING = 'header-row-weighing-';
  protected readonly HEADER_COLUMN_END_WEIGHING = 'header-row-end-weighing';

  protected sectionId: InputSignal<string> = input.required<string>();
  protected trialId: InputSignal<string> = input.required<string>();
  private sectionIdAndTrialIdObject = computed(() => {
    return new SectionIdTrialIdUuid(
      new StringUuid(this.sectionId()),
      new StringUuid(this.trialId())
    );
  });

  private router = inject(Router);
  protected readonly viewModelService = inject(DataStoreReaderService);

  protected model = computed(() => {
    return this.viewModelService.getSectionOverviewViewModel(this.sectionIdAndTrialIdObject());
  });
  protected dataSource = computed(() => {
    const table = new MatTableDataSource(this.model().batchOverviews);
    return table;
  });

  //protected readonly ActionApproveRejectType = ActionApproveRejectType;

  protected displayedColumns = computed(() => {
    const columns = [this.COLUMN_INSERTION_BATCH,
      this.COLUMN_INSERTION_SEX,
      this.COLUMN_INSERTION_AVG_WEIGHT];
    const maxInterimNumber = this.model()?.maxInterimNumber() ?? 0;
    for (let i = 1; i <= maxInterimNumber; i++) {
      columns.push(
        `${this.COLUMN_WEIGHING_DATE}${i}`,
        `${this.COLUMN_WEIGHING_AVG_WEIGHT}${i}`
      )
    }
    columns.push(this.COLUMN_END_WEIGHING_DATE);
    columns.push(this.COLUMN_END_WEIGHING_AVG_WEIGHT);
    return columns;
  });
  protected displayedHeaderColumns = computed(() => {
    const headerColumns = [this.HEADER_COLUMN_INSERTION_BATCH,
      this.HEADER_COLUMN_INSERTION];
    const maxInterimNumber = this.model()?.maxInterimNumber() ?? 0;
    for (let i = 1; i <= maxInterimNumber; i++) {
      headerColumns.push(
        `${this.HEADER_COLUMN_INTERIM_WEIGHING}${i}`
      );
    }
    headerColumns.push(this.HEADER_COLUMN_END_WEIGHING);
    return headerColumns;
  });

  constructor() {
  }

  ngAfterViewInit() {
    const inputSectionId = this.sectionId();
    if (inputSectionId !== this.sectionIdAndTrialIdObject().sectionId.asString()) {
      throw new Error(`sectionId ${inputSectionId} does not match sectionId ${this.sectionIdAndTrialIdObject().sectionId}`);
    }

  }

  // isStatusCompleted(status: WeighingStatus | null): boolean {
  //   if (status === null) {
  //     return false;
  //   }
  //   return status === WeighingStatus.Completed;
  // }

  // getRowClassForInsertionApprove(row: SectionOverviewBatchRowViewModel): string {
  //   const statusCls = this.getRowClassForInsertion(row);
  //   const status = row.insertion?.status;
  //   if (status === WeighingStatus.Completed) {
  //     return `${statusCls} ${this.CLS_CELL_ACTION_GREEN}`;
  //   }
  //   return statusCls;
  // }

  // getRowClassForInsertionReject(row: SectionOverviewBatchRowViewModel): string {
  //   const statusCls = this.getRowClassForInsertion(row);
  //   const status = row.insertion?.status;
  //   if (status === WeighingStatus.Completed) {
  //     return `${statusCls} ${this.CLS_CELL_ACTION_RED}`;
  //   }
  //   return statusCls;
  // }

  getRowClassForInsertion(row: SectionOverviewBatchRowWeighingViewModel): string {
    const status = row.approval?.status;
    if (!status) {
      return this.CLS_CELL_ACTION_GREEN;
    }

    if (status.status === ApprovalStatusTypeEnum.Approved ||
      status.status === ApprovalStatusTypeEnum.Rejected) {
      return this.CLS_REG_COMPLETED;
    }

    if (status.status === ApprovalStatusTypeEnum.Approved) {
      return this.CLS_REG_APPROVED;
    }

    if (status.status === ApprovalStatusTypeEnum.Unknown ||
      status.status === ApprovalStatusTypeEnum.Pending ||
      status.status === ApprovalStatusTypeEnum.NotApplicable) {
      return this.CLS_REG_NOT_APPROVED;
    }

    return this.CLS_CELL_ACTION_GREEN;
  }

  getRowClassForInterimWeighingAvgWeight(row: SectionOverviewBatchRowViewModel, interimSequenceNumber: number): string {
    const previousStatus = interimSequenceNumber === 1 ?
       row.getInitial()?.approval?.status :
        row.getInterimByOrder(interimSequenceNumber - 1)?.approval?.status;
    return this.getRowClass(
      previousStatus,
      row.getInterimByOrder(interimSequenceNumber)?.approval?.status,
      this.CLS_CELL_ACTION_GREEN);
  }

  getRowClassForInterimWeighingDate(row: SectionOverviewBatchRowViewModel, interimSequenceNumber: number): string {
    return this.getRowClass(
      row.getInitial()?.approval?.status,
      row.getInterimByOrder(interimSequenceNumber)?.approval?.status,
      '');
  }

  getRowClassForEndWeighingAvgWeight(row: SectionOverviewBatchRowViewModel): string {
    const previous = row.getInterimOrdered()[row.getInterimOrdered().length - 1];
      return this.getRowClass(
        previous?.approval?.status,
        row.getFinal()?.approval?.status,
        this.CLS_CELL_ACTION_GREEN
      );
  }

  getRowClassForEndWeighingDate(row: SectionOverviewBatchRowViewModel): string {
    const previous = row.getInterimOrdered()[row.getInterimOrdered().length - 1];
      return this.getRowClass(
        previous?.approval?.status,
        row.getFinal()?.approval?.status,
         '');
  }


  getRowClass(
    statusPrev: ApprovalStatusType | undefined,
    statusCurrent: ApprovalStatusType | undefined,
    defaultClsReadyForInput: string
  ): string {
    if (statusPrev?.status !== ApprovalStatusTypeEnum.Approved) {
      return '';
    }

    if (statusCurrent?.status === ApprovalStatusTypeEnum.Approved ||
      statusCurrent?.status === ApprovalStatusTypeEnum.Rejected) {
      return this.CLS_REG_COMPLETED;
    }

    if (statusCurrent?.status === ApprovalStatusTypeEnum.Unknown ||
      statusCurrent?.status === ApprovalStatusTypeEnum.Pending ||
      statusCurrent?.status === ApprovalStatusTypeEnum.NotApplicable) {
      return this.CLS_REG_NOT_APPROVED;
    }

    if (statusCurrent?.status !== ApprovalStatusTypeEnum.Approved) {
      return this.CLS_REG_APPROVED;
    }

    return defaultClsReadyForInput;
  }

  async clickApproveReject(weighingType: WeighingType, evt: ApproveRejectRegClickEvent) {
    if (!evt || !evt.actionType) {
      throw new Error(`clickApproveReject evt is null`);
    }
    //TODO: handle approve / reject
    if(!weighingType) {
      throw new Error(`weighingType is null`);
    }
  }

  async clickBatchWeighing(batchWeighingViewModel: SectionOverviewBatchRowWeighingViewModel | null) {
    if (batchWeighingViewModel === null) {
      throw new Error(`element was null, could not find ${this.ATTR_NAME_BATCH_ID}`);
    }
    const weighingType = batchWeighingViewModel.weighingType.type;

    await this.router.navigate([`/${RoutePaths.BATCH_WEIGHING}/${this.sectionId()}`], {
      queryParams: {
        inputWeighingTypeRaw: weighingType,
        batchWeighingId: batchWeighingViewModel.batchWeighingId.asString(),
      },
    });
  }

  createViewModelForApproveRejectRegComponent(
    batchRowWeighingViewModel: SectionOverviewBatchRowWeighingViewModel
  ): ApproveRejectRegViewModel {
    return {
      id: batchRowWeighingViewModel.batchWeighingId,
      avgWeight: batchRowWeighingViewModel.averageWeight === 0 ? null : batchRowWeighingViewModel.averageWeight,
      status: batchRowWeighingViewModel.approval,
      sectionId: new StringUuid(this.sectionId())
    };
  }

  protected formatAverageWeight(averageWeight: number | null | undefined): number | string {
    return averageWeight === 0 || averageWeight === null || averageWeight === undefined ? '' : averageWeight;
  }
}
